import {AfterViewInit, Directive, Input, OnDestroy} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';
import {RowStateTracker} from '../utils/RowStateTracker';

// Hooks the row/tab highlighting feature onto an existing <dx-data-grid> without touching
// its template's own (onEditingStart)/(onRowPrepared)/... bindings: DevExtreme's
// `instance.on(eventName, handler)` stacks handlers (see feedback_devextreme_on_additive
// memory) instead of replacing whatever the component template already binds, so this can
// subscribe independently in ngAfterViewInit.
@Directive({
  selector: '[appRowStateHighlight]'
})
export class RowStateHighlightDirective implements AfterViewInit, OnDestroy {

  // Undefined on a grid that can't be edited (the template-compare popup reuses the same
  // table components read-only), which leaves that grid untouched.
  @Input('appRowStateHighlight') tracker?: RowStateTracker;

  // Group key the grid's rows belong to, recorded with every row it tracks (see RowStateTracker).
  @Input() appRowStateScope?: any;

  // Another grid whose row being edited also locks this grid's selection (a group grid locks while
  // one of its metrics is edited, since selecting another group would drop that edit).
  @Input() appRowStateLockWith?: RowStateTracker;

  private currentlyEditingKey: any = null;

  constructor(private grid: DxDataGridComponent) {
  }

  ngAfterViewInit(): void {
    const tracker = this.tracker;
    if (!tracker) return;

    const instance = this.grid.instance;

    // DevExtreme applies a click's selection before onRowClick fires and skips it for a target inside
    // .dx-selection-disabled (see _rowClick in m_selection.js), so the class is set right before the
    // click lands: the selection never moves while a row is being edited.
    instance.element().addEventListener('pointerdown', this.lockSelection, true);

    // .on() handlers fire before the template's (onEditingStart), which may set e.cancel to show the
    // discard dialog; defer so a canceled start isn't tracked. Confirming the dialog re-enters via
    // editRow(), and DevExtreme drops the previous row's edit without firing editCanceled, so the
    // previous row has to be cleared here.
    instance.on('editingStart', (e: any) => {
      queueMicrotask(() => {
        if (e.cancel) return;
        const previousKey = this.currentlyEditingKey;
        if (previousKey != null && previousKey !== e.key) {
          tracker.clearEditing(previousKey);
          this.paintRow(previousKey);
        }
        tracker.setEditing(e.key, this.appRowStateScope);
        this.currentlyEditingKey = e.key;
        this.paintRow(e.key);
      });
    });

    // addRow() drops the row being edited without firing editCanceled either; the new row itself
    // is picked up in rowPrepared below.
    instance.on('initNewRow', () => {
      const previousKey = this.currentlyEditingKey;
      if (previousKey == null) return;
      tracker.clearEditing(previousKey);
      this.currentlyEditingKey = null;
      this.paintRow(previousKey);
    });

    instance.on('editCanceled', (e: any) => {
      const key = e.changes[0]?.key ?? this.currentlyEditingKey;
      tracker.clearEditing(key);
      this.currentlyEditingKey = null;
      this.paintRow(key);
    });

    // Swapping the dataSource (group change, CSV import) drops the row being edited without firing
    // editCanceled. The public optionChanged fires before DevExtreme applies the new value, so
    // canceling here still runs against the old rows and every editCanceled listener resets.
    instance.on('optionChanged', (e: any) => {
      if (e.name !== 'dataSource' || e.value === e.previousValue) return;
      if (instance.option('editing.editRowKey') == null && this.currentlyEditingKey == null) return;
      instance.cancelEditData();
    });

    instance.on('rowInserted', (e: any) => {
      tracker.markNew(e.key, this.appRowStateScope);
      this.paintRow(e.key);
    });

    instance.on('rowRemoved', (e: any) => {
      tracker.markRemoved(e.key, this.appRowStateScope);
      if (this.currentlyEditingKey === e.key) this.currentlyEditingKey = null;
    });

    instance.on('saved', (e: any) => {
      // An added row's key can differ from the one it was rendered with (a grid keyed on a
      // backend `id` gets a "_DX_KEY_..." placeholder until the store assigns the real one),
      // so its entry has to be cleared through the key it was actually tracked under.
      if (this.currentlyEditingKey != null) {
        tracker.clearEditing(this.currentlyEditingKey);
        this.currentlyEditingKey = null;
      }

      for (const change of e.changes ?? []) {
        if (change.type === 'update') {
          tracker.clearEditing(change.key);
          tracker.markModified(change.key, this.appRowStateScope);
        } else if (change.type === 'insert') {
          tracker.clearEditing(change.key);
        }
        this.paintRow(change.key);
      }
    });

    // Paints a row's current tracker state whenever DevExtreme (re)builds its DOM node on its
    // own (scroll/sort/filter/paging recreate row elements outright, so this is the only hook
    // that catches it). It is also where an added row is first seen: DevExtreme sets that row's
    // edit key silently (see _addRowCore in m_editing.js), so neither editingStart nor
    // optionChanged fires for it and there is no earlier hook to learn the key from.
    //
    // It never forces a repaint - doing that via repaintRows() raced with DevExtreme's own
    // internal repaint on cancel (see m_editing.js cancelEditData: it repaints before firing
    // onEditCanceled), which is why the handlers above paint the row element directly instead.
    instance.on('rowPrepared', (e: any) => {
      if (e.rowType !== 'data') return;

      if (e.isNewRow && !tracker.isEditing(e.key)) {
        tracker.setEditing(e.key, this.appRowStateScope);
        this.currentlyEditingKey = e.key;
      }

      this.applyRowClasses(e.rowElement, e.key, !!e.isNewRow);
    });
  }

  ngOnDestroy(): void {
    this.grid.instance?.element().removeEventListener('pointerdown', this.lockSelection, true);
    // A tracker can outlive its grid (a parent sharing it across groups keeps the other groups'
    // states); only an edit can't survive the grid, and the owner resets the rest.
    this.tracker?.clearAllEditing();
  }

  private readonly lockSelection = () => {
    const locked = !!this.tracker?.hasEditing() || !!this.appRowStateLockWith?.hasEditing();
    this.grid.instance.element().classList.toggle('dx-selection-disabled', locked);
  };

  private paintRow(key: any): void {
    const index = this.grid.instance.getRowIndexByKey(key);
    if (index < 0) return;
    const isNewRow = !!this.grid.instance.getVisibleRows()[index]?.isNewRow;
    for (const element of this.grid.instance.getRowElement(index) ?? []) {
      this.applyRowClasses(element as HTMLElement, key, isNewRow);
    }
  }

  // `isNewRow` is DevExtreme's own flag for a row that has been added but not yet confirmed.
  // It has to come from the grid rather than from the shape of the key: a grid whose keyExpr
  // is already filled in by (onInitNewRow) - as the device grids do with `_uiKey` - keeps that
  // real key on the unsaved row, so there is nothing about the key itself to go on.
  private applyRowClasses(rowElement: HTMLElement, key: any, isNewRow: boolean): void {
    const tracker = this.tracker;
    if (!tracker) return;

    const dirty = tracker.dirtyOf(key);
    rowElement.classList.toggle('row-state-new', dirty === 'new');
    rowElement.classList.toggle('row-state-modified', dirty === 'modified');
    rowElement.classList.toggle('row-state-editing', tracker.isEditing(key) || isNewRow);
  }
}
